/**
 * Implements a contour tracing algorithm (Moore-Neighbor Tracing) to find the
 * exterior border of a region of cells on a square grid.
 */

import { logger } from '../../../services/logger.client';

export interface Cell {
  row: number;
  col: number;
}

export interface Grid<T = [number, number]> {
  numDirs: number;
  getNeighbor(cell: Cell, dir: number): Cell;
  getVertex(cell: Cell, dir: number): T | undefined;
  getTurn(dir: number, amount: number): number; // +1 for right, -1 for left
}

function traceContour<T>(
  startCell: Cell,
  regionCellSet: Set<string>,
  visitedCells: Set<string>,
  grid: Grid<T>
): T[] | null {
  let startDir: number | null = null;

  // Find the first exterior edge of the starting cell.
  for (let i = 0; i < grid.numDirs; i++) {
    const dir = i;
    const neighbor = grid.getNeighbor(startCell, dir);
    if (!regionCellSet.has(`${neighbor.row}-${neighbor.col}`)) {
      startDir = dir;
      break;
    }
  }

  if (startDir === null) {
    visitedCells.add(`${startCell.row}-${startCell.col}`);
    return null;
  }

  const path: T[] = [];
  let currentCell = startCell;
  let currentDir = startDir;

  let safety = 0;
  const maxIter = regionCellSet.size * 4;

  do {
    logger.debug(`cell: ${currentCell.row},${currentCell.col}, dir: ${currentDir}`)
    if (safety++ > maxIter) {
      logger.error("Contour trace exceeded max iterations. Aborting.");
      return null;
    }

    // Add the vertex corresponding to the direction of the wall.
    const vertex = grid.getVertex(currentCell, currentDir);
    if (vertex) {
      path.push(vertex);
    }
    visitedCells.add(`${currentCell.row}-${currentCell.col}`);

    // To find the next wall segment, we start by looking at the neighbor
    // that is "behind and to the left" of our current wall.
    const backDir = grid.getTurn(currentDir, grid.numDirs / 2); // Opposite direction
    const nextDir = grid.getTurn(backDir, -1); // Turn left

    // Probe clockwise to find the next exterior edge.
    for (let i = 0; i < grid.numDirs; i++) {
      const probeDir = grid.getTurn(nextDir, i);
      const neighbor = grid.getNeighbor(currentCell, probeDir);

      if (regionCellSet.has(`${neighbor.row}-${neighbor.col}`)) {
        // This is an interior edge, so we move to the neighboring cell.
        currentCell = neighbor;
        currentDir = probeDir;
        break;
      }
    }
  } while (
    currentCell.row !== startCell.row ||
    currentCell.col !== startCell.col ||
    currentDir !== startDir
  );

  return path;
}

/**
 * Calculates the exterior contours for a given set of grid cells.
 */
export function calculateGridContours<T>(cells: Cell[], grid: Grid<T>): T[][] {
  if (!cells || cells.length === 0) {
    return [];
  }

  const regionCellSet = new Set((cells ?? []).map(c => `${c.row}-${c.col}`));
  const visitedCells = new Set<string>();
  const contours: T[][] = [];

  const sortedCells = [...cells].sort((a, b) => {
    if (a.row !== b.row) return a.row - b.row;
    return a.col - b.col;
  });

  for (const cell of sortedCells) {
    const cellKey = `${cell.row}-${cell.col}`;
    if (!visitedCells.has(cellKey)) {
      const contour = traceContour(cell, regionCellSet, visitedCells, grid);
      if (contour) {
        contours.push(contour);
      }
    }
  }

  return contours;
}